import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import {setSearchTerm} from "../../redux/slices/organization";
import {useDispatch, useSelector} from "react-redux";

/**
 * Option interface for dropdown items
 * @property value - The value to be used when option is selected
 * @property label - The text to display for the option
 */
interface Option {
  value: string;
  label: string;
}

/**
 * Props for the InfiniteScrollDropdown component
 * @property id - Unique identifier for the dropdown
 * @property name - Form field name
 * @property label - Optional label text above the dropdown
 * @property value - Currently selected value
 * @property placeholder - Text to show when no option is selected
 * @property onChange - Callback function when selection changes
 * @property options - Array of available options
 * @property className - Additional CSS classes
 * @property required - Whether the field is required
 * @property disabled - Whether the dropdown is disabled
 * @property error - Error message to display
 * @property size - Size variant (sm, md, lg)
 * @property showAsterisk - Whether to show the asterisk
 * @property loading - Whether initial data is loading
 * @property loadingMore - Whether more options are being loaded
 * @property hasMore - Whether there are more options to load
 * @property onLoadMore - Callback function to load more options
 * @property onSearch - Callback function for searching options
 * @property searchPlaceholder - Placeholder text for the search input
 */
interface InfiniteScrollDropdownProps {
  id: string;
  name: string;
  label?: string;
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  options: Option[];
  className?: string;
  required?: boolean;
  showAsterisk?: boolean;
  disabled?: boolean;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onSearch?: (searchTerm: string) => void;
  searchPlaceholder?: string;
}

/**
 * InfiniteScrollDropdown Component
 * 
 * A reusable dropdown component with infinite scrolling that provides:
 * - Infinite scrolling for large datasets
 * - Search functionality
 * - Custom styling with Tailwind CSS
 * - Dark mode support
 * - Accessibility features
 * - Multiple size variants
 * - Error state handling
 * - Required field indication
 * - Disabled state support
 * - Click outside to close
 * - Empty option support
 */
const InfiniteScrollDropdown: React.FC<InfiniteScrollDropdownProps> = ({
  id,
  name,
  label,
  value,
  placeholder = 'Select an option',
  onChange,
  options,
  className = '',
  required = false,
  showAsterisk = false,
  disabled = false,
  error,
  size = 'md',
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore = () => {},
  onSearch,
  searchPlaceholder = 'Search...'
}) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [dropUpwards, setDropUpwards] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 420 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const {
    searchTerm
  } = useSelector((state: any) => state.organization);

  // Get currently selected option for display
  const selectedOption = options.find(opt => opt.value === value);

  // Use the infinite scroll hook for loading more options
  const { 
    loadingTriggerRef,
    shouldDisplayLoadingTrigger,
    shouldDisplayLoadingMore
  } = useInfiniteScroll({
    loading,
    loadingMore,
    hasMore,
    onLoadMore,
    enabled: isOpen,
    root: listRef.current
  });

  /**
   * Handles clicks outside the dropdown to close it
   * Adds and removes event listener on mount/unmount
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        dispatch(setSearchTerm(''));
      }
    };

    // Only add the listener when the dropdown is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus the search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current && onSearch) {
      searchInputRef.current.focus();
    }
  }, [isOpen, onSearch]);

  // Calculate dropdown position when it opens or window resizes
  useEffect(() => {
    const calculatePosition = () => {
      if (isOpen && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const dropdownHeight = 420; // Estimated dropdown height
        
        // Calculate available space below
        const spaceBelow = windowHeight - buttonRect.bottom;
        
        // Always position dropdown below the button
        let top = buttonRect.bottom;
        let left = Math.max(0, Math.min(buttonRect.left, windowWidth - buttonRect.width));
        let width = buttonRect.width;
        
        // Calculate actual max height based on available space
        const actualMaxHeight = Math.min(dropdownHeight, spaceBelow - 10); // 10px buffer from bottom
        
        // Never drop upwards - always show below
        setDropUpwards(false);
        setDropdownPosition({ top, left, width, maxHeight: actualMaxHeight > 100 ? actualMaxHeight : dropdownHeight });
      }
    };

    calculatePosition();

    // Add resize listener to recalculate position on window resize
    if (isOpen) {
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition);
      
      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition);
      };
    }
  }, [isOpen]);

  /**
   * Returns appropriate size-based classes
   * @returns Tailwind CSS classes for sizing
   */
  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'p-1.5 text-sm';
      case 'lg': return 'p-3 text-lg';
      default: return 'p-2.5 text-sm';
    }
  };

  /**
   * Handles option selection
   * Updates parent component and closes dropdown
   * @param option - The selected option
   */
  const handleSelect = (option: Option) => {
    onChange(option.value);
    setIsOpen(false);
    dispatch(setSearchTerm(''));
  };

  /**
   * Handles search input change
   * @param e - The input change event
   */
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    dispatch(setSearchTerm(value));
    
    if (onSearch) {
      onSearch(value);
    }
  };

  /**
   * Clears the search input
   */
  const handleClearSearch = () => {
    dispatch(setSearchTerm(''));
    if (onSearch) {
      onSearch('');
    }
  };

  /**
   * Base button styles including:
   * - Conditional styling for states (error, disabled)
   * - Dark mode variants
   * - Focus and hover states
   */
  const baseButtonStyles = `
    w-full text-left rounded-lg border 
    ${error ? 'border-red-500' : 'border-gray-300'} 
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50 hover:bg-gray-100'} 
    text-gray-900
    focus:ring-blue-500 focus:border-blue-500 
    dark:bg-slate-800 dark:border-gray-700 
    dark:placeholder-gray-400 dark:text-white 
    dark:focus:ring-blue-500 dark:focus:border-blue-500
    dark:hover:bg-slate-700
    ${getSizeClasses()}
  `;

  // Skeleton loader for options
  const OptionSkeleton = useCallback(() => (
    <div className="px-4 py-2 animate-pulse">
      <div className="h-4 bg-gray-200 rounded dark:bg-gray-700 w-3/4"></div>
    </div>
  ), []);

  return (
    <div>
      {label && (
        <label htmlFor={id} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {label}
          {showAsterisk && required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <div>
        <div className="relative">
          {/* Main dropdown button */}
          <button
            ref={buttonRef}
            type="button"
            id={id}
            className={`${baseButtonStyles} ${className}`}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <div className="flex items-center justify-between">
              <span className="block truncate">
                {selectedOption ? selectedOption.label : placeholder}
              </span>
              {/* Animated dropdown arrow */}
              <svg 
                className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
                aria-hidden="true" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </button>

          {/* Dropdown menu using portal to avoid clipping */}
          {isOpen && createPortal(
            <div 
              ref={dropdownRef}
              className="fixed z-[9999] bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                maxHeight: `${dropdownPosition.maxHeight}px`
              }}
            >
              {/* Search input */}
              {onSearch && (
                <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <svg 
                        className="w-4 h-4 text-gray-500 dark:text-gray-400" 
                        aria-hidden="true" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        ></path>
                      </svg>
                    </div>
                    <input
                      ref={searchInputRef}
                      type="search"
                      className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                </div>
              )}

              {/* Options list */}
              <ul
                ref={listRef}
                className="py-1 text-sm text-gray-900 dark:text-gray-200 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
                role="listbox"
                aria-labelledby={id}
              >
                {/* Empty option for clearing selection */}
                <li
                  className={`
                    ${!value ? 'bg-gray-100 dark:bg-gray-700' : ''} 
                    px-4 py-2 cursor-pointer
                    hover:bg-gray-100 dark:hover:bg-gray-700
                    text-gray-900 dark:text-gray-200
                    transition-colors duration-150 ease-in-out
                  `}
                  onClick={() => handleSelect({ value: '', label: placeholder })}
                  role="option"
                  aria-selected={!value}
                >
                  {placeholder}
                </li>

                {/* Loading state for initial load */}
                {loading && options.length === 0 ? (
                  <>
                    {[...Array(5)].map((_, idx) => (
                      <OptionSkeleton key={`loading-${idx}`} />
                    ))}
                  </>
                ) : options.length === 0 && searchTerm ? (
                  // No results from search - improved message
                  <li className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex flex-col items-center">
                      <svg 
                        className="w-6 h-6 mb-2 text-gray-400 dark:text-gray-500" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                      <p>No results found for <span className="font-medium">"{searchTerm}"</span></p>
                      <button 
                        onClick={handleClearSearch}
                        className="mt-2 text-blue-500 dark:text-blue-400 hover:underline text-sm"
                      >
                        Clear search
                      </button>
                    </div>
                  </li>
                ) : options.length === 0 ? (
                  // No options available - improved empty state
                  <li className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex flex-col items-center">
                      <svg 
                        className="w-6 h-6 mb-2 text-gray-400 dark:text-gray-500" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        ></path>
                      </svg>
                      <p>No options available</p>
                    </div>
                  </li>
                ) : (
                  // Option items
                  options.map((option) => (
                    <li
                      key={option.value}
                      className={`
                        ${option.value === value ? 'bg-gray-100 dark:bg-gray-700' : ''}
                        px-4 py-2 cursor-pointer
                        hover:bg-gray-100 dark:hover:bg-gray-700
                        text-gray-900 dark:text-gray-200
                        transition-colors duration-150 ease-in-out
                      `}
                      onClick={() => handleSelect(option)}
                      role="option"
                      aria-selected={option.value === value}
                    >
                      {option.label}
                    </li>
                  ))
                )}
                

                {/* Loading more indicator */}
                {shouldDisplayLoadingMore && (
                  <div className="px-4 py-2 pb-8 text-center">
                    <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-blue-600 border-r-transparent dark:border-gray-500"></div>
                    <span className="ml-2 text-sm  text-gray-500 dark:text-gray-400">Loading more...</span>
                  </div>
                )}

                {/* Hidden loading trigger element */}
                {shouldDisplayLoadingTrigger && (
                  <div 
                    ref={loadingTriggerRef} 
                    className="w-full h-8 opacity-0"
                    aria-hidden="true"
                  />
                )}
              </ul>
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Error message display */}
      {error && (
        <p className="mt-1 text-sm text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default InfiniteScrollDropdown; 